/**
 * Prepare permit application (prefill only).
 *
 * Choose a permit type + optional job id, then POST preparePermitApplication. This
 * builds a DRAFT — it does NOT submit, file or attest anything. On success we tell
 * the owner to review and submit it themselves in the jurisdiction portal, then
 * router.back() to the hub.
 *
 * LEGAL / SAFETY: prepare-to-attest. BEAM never auto-submits. NEVER imports
 * @smartbox/* (mobile invariant 1). No raw style values.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listPermitTypes,
  preparePermitApplication,
  type PreparePermitApplicationRequest,
} from '../../../src/api/permits';
import {
  Screen,
  Text,
  Card,
  Button,
  Field,
  Select,
} from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';

export default function PreparePermitScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [permitTypeId, setPermitTypeId] = useState<string | null>(null);
  const [jobId, setJobId] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [projectAddress, setProjectAddress] = useState('');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [prepared, setPrepared] = useState(false);

  const typesQuery = useQuery({
    queryKey: ['permit-types'],
    queryFn: listPermitTypes,
    staleTime: 60_000,
  });

  const prepareMutation = useMutation({
    mutationFn: (body: PreparePermitApplicationRequest) => preparePermitApplication(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['permit-applications'] });
      setPrepared(true);
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not prepare the application.')),
  });

  const onPrepare = () => {
    setFormError(null);
    if (!permitTypeId) {
      setFormError('Choose a permit type to prepare an application.');
      return;
    }
    const body: PreparePermitApplicationRequest = { permit_type_id: permitTypeId };
    if (jobId.trim()) body.job_id = jobId.trim();
    if (applicantName.trim()) body.applicant_name = applicantName.trim();
    if (projectAddress.trim()) body.project_address = projectAddress.trim();
    if (scopeOfWork.trim()) body.scope_of_work = scopeOfWork.trim();
    prepareMutation.mutate(body);
  };

  const types = typesQuery.data ?? [];

  // Success confirmation — make clear BEAM only prepared a draft.
  if (prepared) {
    return (
      <Screen scroll>
        <Text variant="title">Application prepared</Text>
        <Card>
          <Text variant="bodyStrong">Prepared — now submit it yourself.</Text>
          <Text variant="body" color="secondary">
            BEAM built a draft application for you. It has NOT been submitted. Review
            it and submit it yourself in the jurisdiction’s permit portal, then come
            back and mark it submitted so BEAM can track it.
          </Text>
        </Card>
        <Button
          title="Back to permits"
          onPress={() => router.back()}
          accessibilityLabel="Return to the permits hub"
        />
      </Screen>
    );
  }

  return (
    <Screen scroll>
      <Text variant="title">Prepare application</Text>

      <Card>
        <Text variant="small" color="secondary">
          This prepares a DRAFT only. BEAM does not submit, file or attest to anything —
          you’ll review and submit the application yourself in the portal.
        </Text>
      </Card>

      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Permit type
        </Text>
        {typesQuery.isLoading ? (
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading permit types" />
        ) : typesQuery.isError ? (
          <Card>
            <Text variant="body" color="danger">
              {extractErrorMessage(typesQuery.error, 'Failed to load permit types.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => typesQuery.refetch()} />
          </Card>
        ) : types.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No permit types available. Add them in the web app first.
            </Text>
          </Card>
        ) : (
          <Select
            options={types.map((t) => ({
              value: t.id,
              label: t.code ? `${t.name} (${t.code})` : t.name,
            }))}
            value={permitTypeId}
            onChange={setPermitTypeId}
          />
        )}
      </View>

      <Field
        label="Job ID (optional)"
        placeholder="Link to a job"
        value={jobId}
        onChangeText={setJobId}
        autoCapitalize="none"
        autoCorrect={false}
        accessibilityLabel="Job ID"
      />
      <Field
        label="Applicant name (optional)"
        placeholder="Who is applying"
        value={applicantName}
        onChangeText={setApplicantName}
        accessibilityLabel="Applicant name"
      />
      <Field
        label="Project address (optional)"
        placeholder="Where the work happens"
        value={projectAddress}
        onChangeText={setProjectAddress}
        accessibilityLabel="Project address"
      />
      <Field
        label="Scope of work (optional)"
        placeholder="Describe the work"
        value={scopeOfWork}
        onChangeText={setScopeOfWork}
        accessibilityLabel="Scope of work"
      />

      {formError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {formError}
          </Text>
        </Card>
      ) : null}

      <Button
        title="Prepare application (draft only)"
        onPress={onPrepare}
        loading={prepareMutation.isPending}
        disabled={prepareMutation.isPending || !permitTypeId}
        accessibilityLabel="Prepare a draft permit application — does not submit it"
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  section: { gap: theme.spacing.sm },
});
